// reducers/authSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showError } from "../../helpers/toast";

const API = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL, // Replace this with your API's base URL

  // Other default configurations (headers, timeout, etc.) can be set here if needed
});

const initialState = {
  user: null,
  loading: false,
  error: null,
  loginError: null,
  lockedUntil: null,
};

const getLoginErrorPayload = (error) => {
  const data = error?.response?.data || {};
  const message =
    (typeof data.error === "string" && data.error) ||
    (typeof data.message === "string" && data.message) ||
    (typeof data.error?.message === "string" && data.error.message) ||
    "Invalid Credentials";
  const lockedUntil = data.locked_until || data.lockedUntil || null;
  const isLocked =
    Boolean(lockedUntil) ||
    /lock/i.test(message) ||
    error?.response?.status === 423;

  return {
    message,
    lockedUntil,
    isLocked,
  };
};

export const registerUser = createAsyncThunk(
  "/auth/register",
  async (userData) => {
    try {
      const response = await API.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      throw new Error("Failed to register user");
    }
  }
);

export const loginUser = createAsyncThunk(
  "/auth/login",
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await API.post("/auth/login", credentials);
      return response.data;
    } catch (error) {
      return rejectWithValue(getLoginErrorPayload(error));
    }
  }
);

export const logoutUser = createAsyncThunk("/auth/logout", async () => {
  try {
    await API.delete("/auth/logout");
  } catch (error) {
    throw new Error("Failed to log out");
  }
});

export const registerUserData = createAsyncThunk(
  "/users/signup-data",
  async (signupcpde, { rejectWithValue }) => {
    try {
      const response = await API.post("/users/signup-data", signupcpde);
      return response.data;
    } catch (error) {
      // Preserve the original error response
      return rejectWithValue({
        message: error.response?.data?.error || error.response?.data?.message || "Failed to get signup data",
        status: error.response?.status,
        data: error.response?.data
      });
    }
  }
);

export const refreshToken = createAsyncThunk('/auth/refresh-token', async (accessToken) => {
  try {
    const config = {
      headers: {
        "ngrok-skip-browser-warning": "69420",
        Authorization: `${accessToken}`,
        "Content-Type": "application/json",
      },
    };
    const response = await API.get('/auth/refresh-token', config);
    return response.data
    // if(response.data.user.is_admin){
    //   showSuccess('Login Successfully!');
    //   return response.data;
    // }else{
    //   showError('Login Failed!');
    // }

  } catch (error) {
    throw new Error(error);
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearLoginError: (state) => {
      state.loginError = null;
      state.lockedUntil = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUserData.fulfilled, (state, action) => {
        state.user = action.payload;
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.loginError = null;
        state.lockedUntil = null;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
        state.loginError = null;
        state.lockedUntil = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const message = payload.message || "Invalid Credentials";
        state.error = message;
        state.loginError = message;
        state.lockedUntil = payload.lockedUntil || null;
        showError(message);
      })
      .addCase(logoutUser.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
        state.loginError = null;
        state.lockedUntil = null;
      })
      .addCase(logoutUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(refreshToken.fulfilled, (state, action) => {

        state.user.access_token = action.payload?.access_token;
        state.user.exp = action.payload?.exp;

      })
      .addCase(refreshToken.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      })
  },
});

export const { clearLoginError } = authSlice.actions;
export default authSlice.reducer;
